import { useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';

export interface ArchiveCandidate {
  title: string;
  isArchived: boolean;
}

export interface ArchiveConfirmDialogProps {
  /** The item to (un)archive; `null` closes the dialog. */
  candidate: ArchiveCandidate | null;
  /** e.g. "Produkt" – used in the title. */
  entityLabel: string;
  onClose: () => void;
  /** Resolves `true` on success; the dialog only closes then. */
  onConfirm: () => Promise<boolean>;
}

/** Controlled confirm dialog in the Learning Suite admin style (the row may stay visible under the "Alle" filter). */
const ArchiveConfirmDialog = ({ candidate, entityLabel, onClose, onConfirm }: ArchiveConfirmDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const willArchive = !candidate?.isArchived;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const isSuccess = await onConfirm();
      if (isSuccess) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md min-w-[440px] bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-[22px] font-bold text-[#12282A]">
            {entityLabel} {willArchive ? 'archivieren' : 'wiederherstellen'}?
          </DialogTitle>
          <DialogDescription className="text-[14px] text-[#888]">
            {willArchive
              ? `„${candidate?.title}“ wird archiviert und erscheint nur noch unter „Archiviert“. Du kannst es jederzeit wiederherstellen.`
              : `„${candidate?.title}“ wird wiederhergestellt und ist wieder aktiv.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {willArchive ? 'Archivieren' : 'Wiederherstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveConfirmDialog;
